import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Modal, Box, Typography, Grid2, Card, Checkbox, CircularProgress, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ModalBox from '../../themes/overrides/ModalBox';
import { useDispatch } from 'react-redux';
import { getAssets } from '../../actions/mediaAction';
import PropTypes from 'prop-types';
import { useDebounce } from '../useDebounce';

const ExistingMediaUploadPopup = ({ open, onClose, onConfirm, workspaceId }) => {
    const dispatch = useDispatch();
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [assets, setAssets] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const observerTarget = useRef(null);
    const containerRef = useRef();

    const fetchAssetsPage = useCallback(async (pageNumber, search = '') => {
        if (loading) return;
        setLoading(true);
        try {
            const result = await dispatch(getAssets(workspaceId, pageNumber, search));
            if (result && result.assets) {
                setAssets(prev => pageNumber === 1 ? result.assets : [...prev, ...result.assets]);
                setHasMore(result.pagination?.hasNextPage || false);
            }
        } catch (err) {
            console.error('Failed to fetch assets:', err);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [dispatch, workspaceId, loading]);

    // Effect for search
    useEffect(() => {
        if (!open) return;

        setAssets([]);
        setPage(1);
        setHasMore(true);
        fetchAssetsPage(1, debouncedSearchTerm);
    }, [debouncedSearchTerm, open]);

    useEffect(() => {
        if (open) {
            setAssets([]);
            setPage(1);
            setHasMore(true);
            setSearchTerm('');
            fetchAssetsPage(1, '');
        }
    }, [open]);

    useEffect(() => {
        if (!open || !hasMore || loading) return;

        let observer;
        const currentTarget = observerTarget.current;

        if (currentTarget) {
            observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMore && !loading) {
                        setPage(prev => prev + 1);
                    }
                },
                { threshold: 0.1, root: containerRef.current }
            );

            observer.observe(currentTarget);
        }

        return () => {
            if (observer && currentTarget) {
                observer.disconnect();
            }
        };
    }, [open, hasMore, loading, debouncedSearchTerm]);

    useEffect(() => {
        if (page > 1 && hasMore) {
            fetchAssetsPage(page, debouncedSearchTerm);
        }
    }, [page, debouncedSearchTerm]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleAssetSelect = (asset) => {
        setSelectedAssets(prev => {
            const exists = prev.find(a => a._id === asset._id);
            if (exists) return prev.filter(a => a._id !== asset._id);
            return [...prev, asset];
        });
    };

    const handleConfirm = () => {
        onConfirm(selectedAssets);
        setSelectedAssets([]);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox sx={{
                width: { xs: '95%', sm: '600px', md: '700px' },
                maxHeight: { xs: '95vh', sm: '90vh' },
                padding: { xs: 1, md: 3 },
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }} ref={containerRef}>
                <Typography variant="h6" component="h2">Select Media Files</Typography>

                {/* Search Bar */}
                <Box sx={{ mt: 2, mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Search media files..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                height: 44,
                                '& input': {
                                    padding: '6px 8px',
                                    fontSize: 16,
                                },
                            },
                        }}
                    />

                </Box>

                <Box sx={{
                    pt: 2,
                    mb: 3,
                    px: 1,
                    flex: 1,
                    overflowY: 'auto',
                    paddingBottom: '20px'
                }}>
                    <Grid2 container spacing={2} rowGap={4}>
                        {assets.map(asset => (
                            <Grid2 key={asset._id}>
                                <Card
                                    elevation={2}
                                    sx={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                        height: '100%',
                                        outline: selectedAssets.find(a => a._id === asset._id) ? '2px solid #7E5CAD' : 'none',
                                        border: '1px solid #eee',
                                        '&:hover': { outline: '2px solid rgba(0,109,112,0.5)' }
                                    }}
                                    onClick={() => handleAssetSelect(asset)}
                                >
                                    <Box sx={{ width: '190px', height: '100px' }}>
                                        <img
                                            src={asset.url}
                                            alt={asset.title || 'Asset'}
                                            style={{ position: 'absolute', top: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    </Box>

                                    <Checkbox
                                        checked={!!selectedAssets.find(a => a._id === asset._id)}
                                        sx={{ position: 'absolute', top: 8, right: 8, borderRadius: '50%', border: 'none' }}
                                    />
                                </Card>
                                <Typography variant="subtitle1" noWrap sx={{ color: '#2c3e50' }}>
                                    {asset.title || 'Untitled'}
                                </Typography>
                            </Grid2>
                        ))}
                    </Grid2>

                    <div ref={observerTarget} style={{ height: '20px' }} />

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, my: 28 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </Box>

                <Box sx={{
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    pt: 2,
                    borderTop: '1px solid #eee',
                    marginTop: 'auto',
                    zIndex: 1000,
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
                        <Button onClick={handleConfirm} variant="contained" disabled={selectedAssets.length === 0}>
                            Select ({selectedAssets.length})
                        </Button>
                    </Box>
                </Box>
            </ModalBox>
        </Modal>
    );
};

ExistingMediaUploadPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    workspaceId: PropTypes.string
};

export default ExistingMediaUploadPopup;